import React, { useEffect, useRef, useState } from 'react';
import { useTopicStore } from './store';
import ThinkingIndicator from '../../components/ThinkingIndicator';
import type { WikiTopic } from './types/detailsCard.types';
import useHorizontalPointerDrag from './hooks/useHorizontalPointerDrag';

interface WikiImagesSliderV2Props {
  topicId?: string | null;
  viewportWikiLanguage: string;
  onPointerDown?: (e: React.PointerEvent) => void;
}

export default function WikiImagesSliderV2({
  topicId,
  viewportWikiLanguage: _viewportWikiLanguage,
  onPointerDown,
}: WikiImagesSliderV2Props) {
  const wikiTopic = useTopicStore((s) => {
    if (!topicId) return null;
    const t = s.topics[topicId];
    if (!t || t.type !== 'wiki') return null;
    return t as WikiTopic;
  });

  const [displayedImageCount, setDisplayedImageCount] = useState(20);
  const [imageLoadingMore, setImageLoadingMore] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupImageIndex, setPopupImageIndex] = useState(0);
  const [popupZoom, setPopupZoom] = useState(1);
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);
  const imageSliderRef = useHorizontalPointerDrag<HTMLDivElement>();

  useEffect(() => {
    setDisplayedImageCount(20);
    setIsPopupVisible(false);
    setPopupImageIndex(0);
    setPopupZoom(1);
    activePointersRef.current.clear();
    pinchStartDistanceRef.current = null;
    pinchStartZoomRef.current = 1;
  }, [topicId]);

  const images = wikiTopic?.wikiImages ?? [];
  const imagesLoading = !!wikiTopic?.wikiImagesLoading;

  if (!wikiTopic || wikiTopic.pageId === 'prompt') return null;

  const loadMoreImages = () => {
    if (imageLoadingMore || displayedImageCount >= images.length) return;
    setImageLoadingMore(true);
    setTimeout(() => {
      setDisplayedImageCount((prev) => Math.min(prev + 10, images.length));
      setImageLoadingMore(false);
    }, 200);
  };

  const handleImageClick = (index: number) => {
    setPopupImageIndex(index);
    setPopupZoom(1);
    setIsPopupVisible(true);
  };

  const handlePopupClose = () => {
    setIsPopupVisible(false);
    setPopupZoom(1);
    activePointersRef.current.clear();
    pinchStartDistanceRef.current = null;
    pinchStartZoomRef.current = 1;
  };

  const handlePopupPrev = () => {
    setPopupImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setPopupZoom(1);
  };

  const handlePopupNext = () => {
    setPopupImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setPopupZoom(1);
  };

  const zoomIn = () => setPopupZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))));
  const zoomOut = () => setPopupZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))));
  const resetZoom = () => setPopupZoom(1);

  const getDistance = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.hypot(dx, dy);
  };

  const handleZoomSurfacePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointersRef.current.size === 2) {
      const [p1, p2] = Array.from(activePointersRef.current.values());
      pinchStartDistanceRef.current = getDistance(p1, p2);
      pinchStartZoomRef.current = popupZoom;
    }
  };

  const handleZoomSurfacePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!activePointersRef.current.has(e.pointerId)) return;
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointersRef.current.size === 2 && pinchStartDistanceRef.current) {
      const [p1, p2] = Array.from(activePointersRef.current.values());
      const currentDistance = getDistance(p1, p2);
      const scaleRatio = currentDistance / pinchStartDistanceRef.current;
      const nextZoom = Math.min(4, Math.max(1, Number((pinchStartZoomRef.current * scaleRatio).toFixed(2))));
      setPopupZoom(nextZoom);
    }
  };

  const handleZoomSurfacePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    activePointersRef.current.delete(e.pointerId);
    if (activePointersRef.current.size < 2) {
      pinchStartDistanceRef.current = null;
      pinchStartZoomRef.current = popupZoom;
    }
  };

  if (imagesLoading && images.length === 0) {
    return (
      <div className="image-slider-container" onPointerDown={onPointerDown}>
        <ThinkingIndicator style={{ padding: '10px' }} />
      </div>
    );
  }

  if (!images.length) return null;

  return (
    <>
      <div ref={imageSliderRef} className="image-slider-container" onPointerDown={onPointerDown}>
        {images.slice(0, displayedImageCount).map((img, i) => (
          <img
            key={`${img}-${i}`}
            src={img}
            alt={`Article ${i + 1}`}
            className="slider-image"
            loading="lazy"
            decoding="async"
            onClick={() => handleImageClick(i)}
            draggable={false}
            style={{ cursor: 'zoom-in' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ))}
        {displayedImageCount < images.length && (
          <div className="load-more-images-container">
            <button
              className="load-more-images-btn"
              onClick={loadMoreImages}
              disabled={imageLoadingMore}
              style={{ cursor: imageLoadingMore ? 'not-allowed' : 'pointer', opacity: imageLoadingMore ? 0.6 : 1 }}
            >
              {imageLoadingMore
                ? 'Loading...'
                : `Load ${Math.min(10, images.length - displayedImageCount)} more`}
            </button>
          </div>
        )}
      </div>

      {isPopupVisible && images.length > 0 && (
        <div className="image-popup-overlay" onClick={handlePopupClose}>
          <div
            className="image-popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '1100px', height: '80vh', maxHeight: '820px' }}
          >
            <button
              className="popup-close-btn popup-close-btn-near-center"
              onClick={handlePopupClose}
              aria-label="Close image"
            >
              &times;
            </button>
            <button className="popup-nav-btn popup-prev-btn" onClick={handlePopupPrev}>&lt;</button>
            <div
              style={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
              }}
              onPointerDown={handleZoomSurfacePointerDown}
              onPointerMove={handleZoomSurfacePointerMove}
              onPointerUp={handleZoomSurfacePointerEnd}
              onPointerCancel={handleZoomSurfacePointerEnd}
              onWheel={(e) => {
                if (!e.ctrlKey) return;
                e.preventDefault();
                if (e.deltaY < 0) zoomIn();
                else zoomOut();
              }}
            >
              <img
                src={images[popupImageIndex]}
                alt={`Popup ${popupImageIndex + 1}`}
                decoding="async"
                className="popup-image"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${popupZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.12s ease-out',
                  cursor: popupZoom > 1 ? 'grab' : 'zoom-in',
                }}
                onDoubleClick={() => (popupZoom > 1 ? resetZoom() : zoomIn())}
              />
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                gap: '8px',
                zIndex: 1002,
              }}
            >
              <button className="popup-nav-btn" style={{ position: 'static', transform: 'none', fontSize: '20px', padding: '6px 10px' }} onClick={zoomOut}>−</button>
              <button className="popup-nav-btn" style={{ position: 'static', transform: 'none', fontSize: '20px', padding: '6px 10px' }} onClick={resetZoom}>100%</button>
              <button className="popup-nav-btn" style={{ position: 'static', transform: 'none', fontSize: '20px', padding: '6px 10px' }} onClick={zoomIn}>+</button>
            </div>
            <button className="popup-nav-btn popup-next-btn" onClick={handlePopupNext}>&gt;</button>
          </div>
        </div>
      )}
    </>
  );
}
